import { observer } from "mobx-react-lite";
import { Building2, Globe2 } from "lucide-react";
import { useTran } from "@/hooks";
import PillTabs from "@/components/common/PillTabs";
import { MultiSelect } from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { dashboardEChartsStore } from "../../../stores/e-charts.store";
import { dimensionLabels } from "../../../utils/e-charts-dimensions";
import {
  buildPausedPickerHintCopy,
  resolveInteractionState,
} from "../../../utils/e-charts-view";

const ALL = "__ALL__";

const TriggerLabel = ({ label, count }) => (
  <span className="inline-flex items-center gap-1.5">
    {label}
    {count > 0 && (
      <Badge
        variant="secondary"
        className="rounded-full bg-blue-100 px-1.5 py-0 text-[10px] font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
      >
        {count}
      </Badge>
    )}
  </span>
);

// One picker per dimension. Both are always present and both always hold at
// least one item, which is what makes the top-level totals cross-filtered
// rather than single-dimension (YY's linked-filter flow, brief section 1).
const DimensionPicker = observer(({ dimension, options, disabled, hint }) => {
  const { t } = useTran();
  const store = dashboardEChartsStore;
  const labels = dimensionLabels(dimension);
  const selection = store.selectionFor(dimension);
  const isAll = selection.mode === "all";
  const selectedIds = selection.ids.map(String);

  const items = options.map((option) => ({
    value: String(option.id),
    label: option.name,
  }));
  // All is shown as every row ticked plus the All row, so the list reads the
  // way a user expects; the store still holds it as a mode, not as an id list.
  const displayValue = isAll
    ? [ALL, ...items.map((item) => item.value)]
    : selectedIds;

  const handleChange = (next) => {
    // With no real options there is nothing a change can mean, but
    // MultiSelect auto-fires onChange with just the All row when its option
    // list collapses to one entry (org tree transiently empty on refetch).
    // Acting on that would silently reset a user's narrowing to All.
    if (options.length === 0) return;

    const realNext = next.filter((value) => value !== ALL);

    if (!isAll) {
      // Ticking the All row from a subset or from none returns the dimension
      // to All; anything else is a plain subset edit, and emptying it lands
      // in the explicit none mode (data-table convention, Brief D).
      if (next.includes(ALL)) {
        store.setAllEntities(dimension);
        return;
      }
      store.setEntityIds(dimension, realNext);
      return;
    }

    // Already All. Unticking a single row narrows to the rest; unticking the
    // All row itself clears the whole selection to the explicit none state,
    // exactly like the ESG data table filters (YY 2026-08-09, superseding
    // the at-least-one rule).
    if (next.includes(ALL)) {
      store.setEntityIds(dimension, realNext);
    } else {
      store.setEntityIds(dimension, []);
    }
  };

  return (
    <div className="space-y-1">
      <p className="text-[11px] text-muted-foreground">{t(labels.heading)}</p>
      <MultiSelect
        options={[{ value: ALL, label: t("All") }, ...items]}
        value={displayValue}
        onChange={handleChange}
        disabled={disabled}
        // MultiSelect renders the placeholder, not selectedSummary, whenever
        // nothing is ticked, which is exactly the none state; without this
        // override the trigger would read "All ..." while the store is none.
        placeholder={
          selection.mode === "none" ? t("None selected") : t(labels.all)
        }
        searchPlaceholder={t(labels.search)}
        className="min-h-9 w-full"
        maxDisplay={3}
        selectedSummary={
          isAll ? (
            <TriggerLabel label={t(labels.all)} count={0} />
          ) : selection.mode === "none" ? (
            <TriggerLabel label={t("None selected")} count={0} />
          ) : (
            <TriggerLabel
              label={t(labels.heading)}
              count={selectedIds.length}
            />
          )
        }
      />
      {/* Always rendered at one line of height: the paused hint only has
          content during a drill, and mounting it then would grow the filter
          card and shift everything below (standing no-jump rule). */}
      <p className="text-[11px] italic text-muted-foreground/80">
        {hint ?? "\u00A0"}
      </p>
    </div>
  );
});

const EChartsFilters = observer(({ buOptions, countryOptions }) => {
  const { t } = useTran();
  const store = dashboardEChartsStore;
  const drillContext = store.drillContext;

  const groupByTabs = [
    { value: "bu", label: t("Business Unit"), icon: Building2 },
    { value: "country", label: t("Country"), icon: Globe2 },
  ];

  // Paused versus live (brief 3.2 item 10): the CLICKED dimension's picker is
  // disabled so the selection the breadcrumb promises to restore cannot be
  // edited out from under the return trip. The other dimension stays fully
  // live and re-filters the drilled view in place. The group-by pill stays
  // active because exiting the drill is exactly what it is meant to do.
  const { pausedDimension } = resolveInteractionState({ drillContext });
  const pausedHint = buildPausedPickerHintCopy({ drillContext });
  const hintFor = (dimension) =>
    pausedDimension === dimension && pausedHint
      ? t(pausedHint.key, pausedHint.params)
      : null;

  return (
    <div className="space-y-3 rounded-md border border-border/60 bg-muted/20 p-3">
      <div className="flex items-center gap-2">
        {/* Label only: the switch still groups the bars by dimension, but
            YY read "Group by" as an aggregation control, so the client-facing
            wording is "View by" (LINE 2026-08-08, her one requested change
            from the item 5 staging review). Internal names stay groupBy. */}
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t("View by")}
        </span>
        <PillTabs
          tabs={groupByTabs}
          value={store.groupBy}
          onChange={store.setGroupBy}
          size="sm"
        />
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <DimensionPicker
          dimension="bu"
          options={buOptions}
          disabled={pausedDimension === "bu"}
          hint={hintFor("bu")}
        />
        <DimensionPicker
          dimension="country"
          options={countryOptions}
          disabled={pausedDimension === "country"}
          hint={hintFor("country")}
        />
      </div>
    </div>
  );
});

export default EChartsFilters;
